import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { MaterialIcon } from "@/components/MaterialIcon";

const MAX_POLLS = 10;

const Card = ({ children, testId }) => (
  <div
    className="mt-[56px] md:mt-[88px] mx-auto w-full max-w-[860px] rounded-[20px] border border-[#E0E0E0] bg-white px-6 py-12 md:px-16 md:py-[68px] text-center df-fade-up"
    data-testid={testId}
  >
    {children}
  </div>
);

const ReturnButton = ({ festivalId, children }) => (
  <Link
    to={festivalId ? `/festivals/${festivalId}` : "/festivals"}
    className="mt-9 inline-block rounded-full bg-[#F8D481] hover:bg-[#f3c968] px-12 py-4 text-[16px] font-bold transition-colors"
    data-testid="payment-return-button"
  >
    {children}
  </Link>
);

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: sessionId ? "checking" : "missing" });
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const check = async () => {
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ phase: "paid", ...data });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: data.payment_status, ...data });
        if (polls.current >= MAX_POLLS) return setState({ phase: "timeout", ...data });
      } catch (e) {
        if (polls.current >= MAX_POLLS) return setState({ phase: "error" });
      }
      timer = setTimeout(check, 2000);
    };
    check();
    return () => clearTimeout(timer);
  }, [sessionId]);

  if (state.phase === "checking") {
    return (
      <Card testId="payment-checking">
        <MaterialIcon name="progress_activity" size={64} weight={300} className="text-[#C9C9C9] animate-spin" />
        <h1 className="mt-5 text-[24px] md:text-[28px] font-bold leading-tight">Confirming your payment…</h1>
        <p className="mt-3 text-[16px] leading-[1.5] text-[#808080]">This usually takes a couple of seconds.</p>
      </Card>
    );
  }

  if (state.phase === "paid") {
    return (
      <Card testId="payment-success">
        <MaterialIcon name="check_circle" size={68} weight={400} className="text-[#2BC21E]" data-testid="payment-success-icon" />
        <h1 className="mt-5 text-[24px] md:text-[28px] font-bold leading-tight" data-testid="payment-success-title">
          You’re going to {state.festival_name}
        </h1>
        <p className="mt-3 text-[16px] leading-[1.5] text-[#808080]" data-testid="payment-receipt-note">
          Your pass has been claimed and an
          <br className="hidden md:block" /> email confirmation has been sent to {state.buyer_email}
        </p>
        <ReturnButton festivalId={state.festival_id}>Return to Festival Details</ReturnButton>
      </Card>
    );
  }

  const messages = {
    missing: ["No payment to confirm", "This page needs a checkout session to verify."],
    expired: ["Checkout expired", "Your session timed out before payment. You can try again from the festival page."],
    failed: ["Payment failed", "Your card was not charged. Please try again."],
    timeout: ["Still processing", "Your payment is taking longer than usual. If you were charged, your pass is confirmed — refresh in a moment."],
    error: ["Something went wrong", "We couldn't verify the payment right now. Please refresh."],
  };
  const [title, body] = messages[state.phase] || messages.error;
  return (
    <Card testId={`payment-${state.phase}`}>
      <MaterialIcon name="error" size={64} weight={300} className="text-[#C9C9C9]" />
      <h1 className="mt-5 text-[24px] md:text-[28px] font-bold leading-tight">{title}</h1>
      <p className="mt-3 text-[16px] leading-[1.5] text-[#808080]">{body}</p>
      <ReturnButton festivalId={state.festival_id}>{state.festival_id ? "Return to Festival Details" : "Return to Festivals"}</ReturnButton>
    </Card>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const festivalId = params.get("festival");
  return (
    <Card testId="payment-cancel">
      <MaterialIcon name="cancel" size={64} weight={300} className="text-[#C9C9C9]" />
      <h1 className="mt-5 text-[24px] md:text-[28px] font-bold leading-tight">Checkout cancelled</h1>
      <p className="mt-3 text-[16px] leading-[1.5] text-[#808080]">No charge was made. Your pass is still waiting for you.</p>
      <ReturnButton festivalId={festivalId}>{festivalId ? "Return to Festival Details" : "Return to Festivals"}</ReturnButton>
    </Card>
  );
}
